import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { Automaticrentrenewal } from 'src/app/services/automaticrentrenewal';
import { MarketInfoService } from 'src/app/services/marketInfo.service';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { PDFDocument } from 'pdf-lib';
import { RentalSettingService } from 'src/app/services/RentalSetting.service';
import { IRentalSettings } from 'src/app/Shared/Interfaces/IRentalSettings';
import { GeneralService } from 'src/app/services/general.service';
import { DatePipe } from '@angular/common';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-automatic-rental-renewal',
  standalone: true,
  imports: [TopBarComponent, SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, LoaderComponent],
  providers: [DatePipe],
  templateUrl: './ops-automatic-rental-renewal.html',
  styleUrl: './ops-automatic-rental-renewal.scss'
})

export class OpsAutomaticRentalRenewal implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  public loaderIcon: Promise<boolean>;
  tabHeaderTitle: string = 'Automatic Rent Renewal';
  isCollapsed: boolean = true;
  selectedrenewalType: any;
  rentRenewalForm: FormGroup;
  rentalTypeIds: number[] = [];
  showCombinedMonthly = false;
  showCombinedWeekly = false;
  RentaltypeList: any;
  selectedrenewalTypeID: number = 4;
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  showPdfModal = false;
  allBase64Strings: string[] = [];
  mergedPdfUrl: string = '';
  loading = false;
  activeTab: 'ARR' | 'VOID' = 'ARR';
  monthOptions: { label: string; value: number }[] = [];
  maxBookingMonths = 1;
  maxBookingWeeks = 1;
  maxAdvBookingDays = 1;
  numberOfMonths: number[] = [];
  numberOfWeeks: number[] = [];
  weekOptions: { label: string; value: Date }[] = [];
  marketCurrentDate!: Date;
  minExpiryDate!: Date;

  constructor(
    private fb: FormBuilder,
    private marketInfoService: MarketInfoService,
    private generalService: GeneralService,
    private rentalService: RentalSettingService,
    private automaticRentRenewalService: Automaticrentrenewal,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadMarketCurrentDate();
    this.loadRentalSettings();
    this.getRentalType();
  }

  initForm(): void {
    this.rentRenewalForm = this.fb.group({
      rentalType: ['monthly'],
      renewalDate: [null],
      startMonth: [''],//CM
      numMonths: [1],//CM
      startWeek: [''], //CW
      numWeeks: [1],//CW
      treatRentalChargesAs: [0], // Paid = 1, Due = 0
      recurringCharges: [true],
      excludeDealers: [true],
      printReceipt: [false],
      emailReceipt: [false],
      rentalNotes: [''],
      voidFromReceiptId: [null, Validators.required],
      voidToReceiptId: [null, Validators.required],
      reasonForVoid: ['', Validators.required]
    });
  }

  onTabChange(tab: string): void {
    if (tab === 'Void') {
      this.activeTab = 'VOID';
      this.tabHeaderTitle = 'Void Automatic Rent Renewal';
    } else {
      this.activeTab = 'ARR';
      this.tabHeaderTitle = 'Automatic Rent Renewal';
    }
  }

  onPrimaryAction(): void {
    if (this.activeTab === 'VOID') {
      this.onProceedWithVoidARR();
    } else {
      this.onProceedWithRenewal();
    }
  }

  getMonthNumber(monthYear: string): number {
    if (!monthYear) return 0;
    const monthPart = monthYear.split(' ')[0]; // "Jan"
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return months.indexOf(monthPart) + 1;
  }

  generateNextFourMonths(): void {
    this.monthOptions = [];

    const today = new Date();
    let month = today.getMonth(); // 0-based
    let year = today.getFullYear();
    for (let i = 0; i < 4; i++) {
      const date = new Date(year, month + i, 1);
      const label = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      const value = date.getMonth() + 1; // Jan = 1, Feb = 2 ...
      this.monthOptions.push({
        label,
        value
      });
    }
  }

  getRentalType() {
    this.loaderIcon = Promise.resolve(true);
    this.marketInfoService.getRentalType().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res) {
          this.RentaltypeList = res;
          this.rentalTypeIds = this.RentaltypeList.split(',').map((x: string) => Number(x.trim()));
          this.showCombinedMonthly = this.rentalTypeIds.includes(4);
          this.showCombinedWeekly = this.rentalTypeIds.includes(5);
        }
        this.loaderIcon = Promise.resolve(false);
      },
      error: (err) => {
        this.loaderIcon = Promise.resolve(false);
      }
    })
  }

  loadMarketCurrentDate(): void {
    this.generalService.GetMarketCurrentTime().pipe(takeUntil(this.destroy$)).subscribe({
      next: (marketTime: string) => {
        this.marketCurrentDate = marketTime ? this.toLocalDate(marketTime.split('T')[0]) : this.getDateOnly();
        this.minExpiryDate = this.marketCurrentDate;
        // Set renewal date to market date 
        this.rentRenewalForm.patchValue({
          renewalDate: this.marketCurrentDate
        });
      },
      error: (err) => {
        this.marketCurrentDate = this.getDateOnly();
        this.minExpiryDate = this.marketCurrentDate;
        this.rentRenewalForm.patchValue({
          renewalDate: this.marketCurrentDate
        });
        console.error("Error fetching market time", err);
      }
    });
  }

  toLocalDate(dateString: string): Date {
    const [y, M, d] = dateString.split('-').map(Number);
    return new Date(y, M - 1, d);
  }

  private getDateOnly(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  loadRentalSettings(): void {
    this.rentalService.getRentalSettings().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: IRentalSettings) => {
        const bookingSettings = data.BookingReservationSettings;
        this.maxBookingMonths = bookingSettings.MaxBookingMonths ?? 1;
        this.maxBookingWeeks = bookingSettings.MaxBookingWeeks ?? 1;
        this.maxAdvBookingDays = bookingSettings.MaxAdvBookingDays ?? 1;
        // Generate dropdowns after getting API values
        this.generateMonthOptions();
        this.updateNumberOfMonths();
        this.generateWeekOptions();
        this.updateNumberOfWeeks();

      },
      error: err => {
        console.error('Failed to load rental settings', err);
      }
    });
  }

  generateMonthOptions(): void {
    this.monthOptions = [];
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    // Current month + maxBookingMonths future months
    for (let i = 0; i <= this.maxBookingMonths; i++) {
      const date = new Date(currentYear, currentMonth + i, 1);

      this.monthOptions.push({
        label: date.toLocaleString('en-US', {
          month: 'short',
          year: 'numeric'
        }),
        value: date.getMonth() + 1
      });
    }
    // Default to current month
    if (this.monthOptions.length > 0) {
      this.rentRenewalForm.patchValue({
        startMonth: this.monthOptions[0].label
      });
    }
  }

  onStartMonthChange(): void {
    this.updateNumberOfMonths();
  }

  updateNumberOfMonths(): void {
    const selectedMonth = this.rentRenewalForm.get('startMonth')?.value;

    if (!selectedMonth || this.monthOptions.length === 0) {
      this.numberOfMonths = [];
      return;
    }

    const selectedIndex = this.monthOptions.findIndex(x => x.label === selectedMonth);

    if (selectedIndex === -1) {
      this.numberOfMonths = [];
      return;
    }

    const availableMonths = this.monthOptions.length - selectedIndex;
    this.numberOfMonths = Array.from({ length: availableMonths }, (_, i) => i + 1);
    this.rentRenewalForm.patchValue({
      numMonths: 1
    });
  }

  generateWeekOptions(): void {
    this.weekOptions = [];

    const today = new Date();
    // Get Monday of current week
    const currentDay = today.getDay();
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay;

    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() + diffToMonday);
    currentMonday.setHours(0, 0, 0, 0);

    // Current week + maxBookingWeeks future weeks
    for (let i = 0; i <= this.maxBookingWeeks; i++) {

      const startDate = new Date(currentMonday);
      startDate.setDate(currentMonday.getDate() + (i * 7));

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      const start = startDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      const end = endDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      const year = endDate.getFullYear();
      const label = `${start} to ${end}, ${year}`;
      this.weekOptions.push({ label, value: startDate });
    }

    // Default to current week
    if (this.weekOptions.length > 0) {
      this.rentRenewalForm.patchValue({
        startWeek: this.weekOptions[0].label
      });

      this.updateNumberOfWeeks();
    }
  }

  onStartWeekChange(): void {
    this.updateNumberOfWeeks();
  }

  updateNumberOfWeeks(): void {
    const selectedWeek = this.rentRenewalForm.get('startWeek')?.value;

    if (!selectedWeek || this.weekOptions.length === 0) {
      this.numberOfWeeks = [];
      return;
    }

    const selectedIndex = this.weekOptions.findIndex(
      x => x.label === selectedWeek
    );

    if (selectedIndex === -1) {
      this.numberOfWeeks = [];
      return;
    }

    const availableWeeks = this.weekOptions.length - selectedIndex;

    this.numberOfWeeks = Array.from(
      { length: availableWeeks },
      (_, i) => i + 1
    );

    this.rentRenewalForm.patchValue({
      numWeeks: 1
    });
  }

  onProceedWithRenewal() {
    const renewalDate = this.rentRenewalForm.get('renewalDate')?.value;
    const dateOfRenewal = this.datePipe.transform(renewalDate, 'yyyy-MM-dd') ?? this.datePipe.transform(this.marketCurrentDate, 'yyyy-MM-dd')!;

    const notesValue = this.rentRenewalForm.get('rentalNotes')?.value;
    const noofmonths = this.rentRenewalForm.get('numMonths')?.value;
    const treatRentalValue = this.rentRenewalForm.get('treatRentalChargesAs')?.value;
    const PrintRentRenewalReceipt = this.rentRenewalForm.get('printReceipt')?.value;
    const Renewothercharges = this.rentRenewalForm.get('recurringCharges')?.value;
    const ExcludeflagARR = this.rentRenewalForm.get('excludeDealers')?.value;
    const rentalType = this.rentRenewalForm.get('rentalType')?.value;
    const monthName = this.rentRenewalForm.get('startMonth')?.value;
    const sendEmail = !!this.rentRenewalForm.get('emailReceipt')?.value;
    //const year = renewalDate.getFullYear();
    const month = this.getMonthNumber(monthName);
    const today = new Date(this.marketCurrentDate);
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1–12
    const year = month < currentMonth ? currentYear + 1 : currentYear;
    const startyearmonth = `${year}${month < 10 ? '0' : ''}${month}`;
    let receiptHasError = false;
    let paid: boolean = treatRentalValue === 1 ? true : treatRentalValue === 0 ? false : false;
    //console.log(`${startyearmonth},${month}`)
    const vendorSeen = new Set<number>();
    const orderedVendorIds: number[] = [];
    Swal.fire({
      title: $localize`Saving`,
      text: $localize`Are you sure you want to proceed with the Automatic Rent Renewal?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Ok`
    }).then(confirmResult => {
      if (!confirmResult.isConfirmed) {
        console.log('Renewal declined');
        return;
      }

      this.loaderIcon = Promise.resolve(true);

      if (rentalType === 'monthly') {
        this.automaticRentRenewalService.GetCombinedMonthlyReservationforARR(startyearmonth, noofmonths).pipe(takeUntil(this.destroy$))
          .subscribe({
            next: res => {
              const reservations = res?.CMonthlyBookings ?? [];
              const otherCharges = res?.UpdatedOtherRentalCharges ?? [];
              if (reservations.length === 0) {
                Swal.fire({
                  title: $localize`No Reservations`,
                  text: $localize`No reserved Booths are available!`,
                  icon: 'info',
                  confirmButtonText: $localize`OK`
                });
                this.loaderIcon = Promise.resolve(false);
                return;
              }

              const groupByVendor = (items: any[], key: string) =>
                items.reduce((acc, item) => {
                  const id = item[key];
                  if (!acc[id]) acc[id] = [];
                  acc[id].push(item);
                  return acc;
                }, {} as Record<number, any[]>);

              reservations.forEach(r => {
                if (!vendorSeen.has(r.cmr_num_VendorId)) {
                  vendorSeen.add(r.cmr_num_VendorId);
                  orderedVendorIds.push(r.cmr_num_VendorId);
                }
              });

              const groupedReservations = groupByVendor(reservations, 'cmr_num_VendorId');
              const groupedOtherCharges = groupByVendor(otherCharges, 'VendorId');
              const vendorIds = orderedVendorIds;
              const chunkSize = 20;
              let allIdList: number[] = [];
              let anyChunkSuccess = false;
              let anyChunkFailed = false;

              const processChunks = (index: number) => {
                if (index >= vendorIds.length) {//at the end when index is greater than or = total vendor ids
                  if (anyChunkSuccess && !anyChunkFailed) { //All chunks success,
                    this.toastr.success(
                      $localize`Automatic Rental Renewal completed successfully`,
                      $localize`Success`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                  }
                  else if (anyChunkSuccess && anyChunkFailed) {// some chunks fails, some succuess
                    this.toastr.warning(
                      $localize`Partial success: some reservations could not be processed.`,
                      $localize`Warning`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                  }
                  else { // all failed
                    this.toastr.error(
                      $localize`Automatic Rental Renewal failed for all reservations.`,
                      $localize`Error`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                  }

                  if (anyChunkSuccess && (PrintRentRenewalReceipt || sendEmail)) {
                    processReceipts(0);
                  } else {
                    this.loaderIcon = Promise.resolve(false);
                  }

                  return;
                }

                const chunkVendorIds = vendorIds.slice(index, index + chunkSize);
                const chunkReservations: any[] = [];
                const chunkOtherCharges: any[] = [];

                chunkVendorIds.forEach(vendorId => {
                  if (groupedReservations[vendorId]) {
                    chunkReservations.push(...groupedReservations[vendorId]);
                  }

                  if (groupedOtherCharges[vendorId]) {
                    chunkOtherCharges.push(...groupedOtherCharges[vendorId]);
                  }
                });

                this.automaticRentRenewalService.saveCombinedMonthlyRentalsARR(
                  startyearmonth,
                  noofmonths,
                  Renewothercharges,
                  ExcludeflagARR,
                  notesValue,
                  chunkReservations,
                  chunkOtherCharges,
                  paid,
                  dateOfRenewal
                ).subscribe({
                  next: response => {
                    if (response?.Success && response?.IdList?.length > 0) {
                      allIdList = allIdList.concat(response.IdList);
                      anyChunkSuccess = true;
                    } else if (response?.Success && response.IdList.length === 0) {
                      Swal.fire({
                        title: $localize`No Reservations`,
                        text: $localize`No reserved Booths are available!`,
                        icon: 'info',
                        confirmButtonText: $localize`OK`
                      });
                      this.loaderIcon = Promise.resolve(false);
                      return;
                    } else {
                      anyChunkFailed = true;
                    }

                    processChunks(index + chunkSize);
                  },
                  error: () => {
                    this.loaderIcon = Promise.resolve(false);
                    this.toastr.error($localize`Something went wrong while saving reservations.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
                    anyChunkFailed = true;
                  }
                });
              };

              const processReceipts = async (receiptIndex: number) => {
                if (receiptIndex >= allIdList.length) {
                  this.loaderIcon = Promise.resolve(false);
                  if (this.allBase64Strings.length > 0 && PrintRentRenewalReceipt) {
                    this.loading = true;
                    this.openModalById('previewAttachmentModal');
                    try {
                      if (this.mergedPdfUrl) {
                        URL.revokeObjectURL(this.mergedPdfUrl);
                        this.mergedPdfUrl = '';
                      }
                      if (PrintRentRenewalReceipt) {
                        this.mergedPdfUrl = await this.mergeAllBase64Pdfs();
                        this.loading = false;
                        setTimeout(() => { this.updateIframeSrc(); }, 300);
                      }
                    } catch (err) {
                      console.error(err);
                      this.loading = false;
                    }

                  } else {
                    if (!receiptHasError && PrintRentRenewalReceipt) {
                      this.toastr.info('No receipts available');
                    }
                  }
                  return;
                }
                const chunkIds = allIdList.slice(receiptIndex, receiptIndex + chunkSize);
                this.automaticRentRenewalService.displayARRReceipts(chunkIds, this.selectedrenewalTypeID, sendEmail)
                  .subscribe({
                    next: res => {
                      if (res.Success) {
                        this.allBase64Strings.push(res.Message.replace(/^data:.*;base64,/, ''));
                      } else {
                        receiptHasError = true;
                        this.toastr.error($localize`Something went wrong while displaying the Receipts.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
                      }
                      processReceipts(receiptIndex + chunkSize);
                    },
                    error: () => {
                      receiptHasError = true;
                      this.toastr.error($localize`Something went wrong while displaying the Receipts.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
                      processReceipts(receiptIndex + chunkSize);
                    }
                  });
              };
              this.allBase64Strings = [];
              this.loading = false;

              processChunks(0);
            },
            error: err => {
              this.toastr.error($localize`Failed to retrieve reservations.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              this.loaderIcon = Promise.resolve(false);
            }
          });
      } else if (rentalType === 'weekly') {
        // Combined Weekly Rent Renewal will be implemented in a future release
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  updateIframeSrc() {
    const iframe = document.getElementById('previewprintf') as HTMLIFrameElement;
    //console.log("Iframe found:", !!iframe);
    //console.log("Merged URL:", this.mergedPdfUrl);
    if (iframe) iframe.src = '';
    if (iframe && this.mergedPdfUrl) {
      iframe.src = this.mergedPdfUrl;
    }
  }

  onProceedWithVoidARR(): void {
    if (this.rentRenewalForm.invalid) {
      this.rentRenewalForm.markAllAsTouched();
      this.toastr.warning(
        'Please enter From Receipt ID, To Receipt ID and Reason for Void.',
        'Validation Required',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const fromId = Number(this.rentRenewalForm.get('voidFromReceiptId')?.value);
    const toId = Number(this.rentRenewalForm.get('voidToReceiptId')?.value);

    if (toId < fromId) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Receipt Range',
        text: $localize`To Receipt ID (${toId}) cannot be less than From Receipt ID (${fromId})`,
        confirmButtonText: 'Ok'
      });
      return;
    }

    Swal.fire({
      title: $localize`Confirm Void`,
      text: $localize`Are you sure you want to proceed with Void Automatic Rent Renewal?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Ok`,
      cancelButtonText: $localize`Cancel`
    }).then(confirmResult => {

      if (!confirmResult.isConfirmed) {
        return;
      }

      const payload = {
        FromReceiptId: Number(this.rentRenewalForm.get('voidFromReceiptId')?.value),
        ToReceiptId: Number(this.rentRenewalForm.get('voidToReceiptId')?.value),
        VoidARRReason: this.rentRenewalForm.get('reasonForVoid')?.value
      };

      this.loaderIcon = Promise.resolve(true);

      this.automaticRentRenewalService
        .voidARRTransactions(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const message = res?.Message || '';
            const isSuccess = res?.Success ?? true;

            if (isSuccess) {
              this.toastr.success(message, 'Success', this.sharedHTMLSettings.ToasterConfig);
            } else {
              this.toastr.warning(message, 'Warning', this.sharedHTMLSettings.ToasterConfig);
            }

            this.loaderIcon = Promise.resolve(false);
          },
          error: (err) => {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.error(err?.message || 'Something went wrong', 'Error', this.sharedHTMLSettings.ToasterConfig);
          }
        });

    });
  }

  openModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (!modalEl) return;

    let modal = window['bootstrap'].Modal.getInstance(modalEl);
    if (!modal) {
      modal = new window['bootstrap'].Modal(modalEl, {
        backdrop: 'static',
        keyboard: false
      });
    }

    modal.show();
  }

  closeModalById(modalId: string) {
    const iframe = document.getElementById('previewprintf') as HTMLIFrameElement;
    if (iframe) iframe.src = '';

    if (this.mergedPdfUrl) {
      URL.revokeObjectURL(this.mergedPdfUrl);
      this.mergedPdfUrl = '';
    }

    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      modalInstance?.hide();
    }

    document.body.classList.remove('modal-open');
    document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
  }


  async mergeAllBase64Pdfs(): Promise<string> {
    console.log("Total PDFs received:", this.allBase64Strings.length);
    const mergedPdf = await PDFDocument.create();

    for (let i = 0; i < this.allBase64Strings.length; i++) {
      const base64Pdf = this.allBase64Strings[i];
      const cleanBase64 = base64Pdf.includes("base64,") ? base64Pdf.split("base64,")[1] : base64Pdf;
      const binary = atob(cleanBase64);
      const pdfBytes = new Uint8Array(binary.length);
      for (let j = 0; j < binary.length; j++) {
        pdfBytes[j] = binary.charCodeAt(j);
      }
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
      copiedPages.forEach(p => mergedPdf.addPage(p));
    }

    //console.log("Merged page count:", mergedPdf.getPageCount());
    const mergedBytes = await mergedPdf.save();
    //console.log("Merged bytes length:", mergedBytes.length);
    const safeBytes = new Uint8Array(mergedBytes);
    const blob = new Blob([safeBytes], { type: "application/pdf" });
    return URL.createObjectURL(blob);

  }

  base64ToBlob(base64: string, contentType: string) {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  resetForm(): void {
    this.rentRenewalForm.reset({
      renewalDate: null,
      startMonth: null,
      numMonths: [1],
      rentalType: 'monthly',
      treatRentalChargesAs: [0],
      recurringCharges: false,
      excludeDealers: false,
      printReceipt: false,
      emailReceipt: false,
      rentalNotes: ''
    });
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  closeARRPage(): void {
    this.rentRenewalForm.reset({
      rentalType: 'monthly',
      treatRentalChargesAs: [0]
    });
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
